import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';

import { Box, Button, ButtonGroup, Divider, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { MAutocomplete } from 'common';
import { RECIPIENT_REQUIRED } from 'utils/helpers/validationMessages';

import {
  buttonBoxStyles,
  buttonLabelStyles,
  chainedCustomerDivStyles,
  recipientRequiredStyles,
  sectionDivStyles,
} from './SelectCustomerChain.styles';

// getIconsColorAsRGBA :: MuiTheme -> String
// Provided an Mui's Theme returns the `palette.graphic.icons` color an rgba color string
// right now expects `palette.graphic.icons` with the following structure '#color, percentage%'
const getIconsColorAsRGBA = R.compose(
  // eslint-disable-next-line react-hooks/rules-of-hooks
  R.apply(R.useWith(alpha, [R.identity, R.o(R.divide(R.__, 100), parseFloat)])),
  R.split(','),
  R.path(['palette', 'graphic', 'icons'])
);
const closeIconStyles = {
  color: getIconsColorAsRGBA,
  fontSize: '24px',
};
const ChainedCustomer = props => {
  const { affiliateId, affiliateName, index, maxIndex, onClickRemove, onMoveUp, onMoveDown } =
    props;
  const theme = useTheme();

  return (
    <>
      <Box sx={{ ...chainedCustomerDivStyles(theme) }}>
        <Typography
          sx={{
            overflow: 'hidden',
            paddingTop: 2,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: 190,
          }}
          variant="body1"
        >
          {affiliateName}
        </Typography>
        <ButtonGroup
          disableElevation
          orientation="horizontal"
          size="small"
          sx={buttonBoxStyles}
          variant="outlined"
        >
          <Button
            aria-label="Move up"
            disabled={index === 0}
            onClick={() => {
              onMoveUp(affiliateId);
            }}
            variant="outlined"
          >
            <ArrowUpwardRoundedIcon sx={buttonLabelStyles} />
          </Button>
          <Button
            aria-label="Move down"
            disabled={index === maxIndex}
            onClick={() => {
              onMoveDown(affiliateId);
            }}
            variant="outlined"
          >
            <ArrowDownwardRoundedIcon sx={buttonLabelStyles} />
          </Button>
          <Button
            aria-label="Remove sequence"
            onClick={() => {
              onClickRemove(affiliateId);
            }}
            variant="text"
          >
            <CloseRoundedIcon sx={closeIconStyles} />
          </Button>
        </ButtonGroup>
      </Box>
      <Divider sx={{ width: '100%' }} />
    </>
  );
};

ChainedCustomer.propTypes = {
  affiliateId: PropTypes.number.isRequired,
  affiliateName: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  maxIndex: PropTypes.number.isRequired,
  onClickRemove: PropTypes.func,
  onMoveDown: PropTypes.func,
  onMoveUp: PropTypes.func,
};

ChainedCustomer.defaultProps = {
  onClickRemove: () => {},
  onMoveDown: () => {},
  onMoveUp: () => {},
};

const SelectCustomerChain = props => {
  const { affiliateOptions, data, setValue, onAllRequiredFieldsSet } = props;
  const { recipientIdName, chainContactIds } = data;
  const theme = useTheme();

  useEffect(() => {
    onAllRequiredFieldsSet(chainContactIds.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainContactIds]);

  const handleAddChain = ({ value }) => {
    const update = [...chainContactIds];
    update.push(parseInt(value, 10));
    setValue('chainContactIds', update);
  };

  const handleRemoveChain = affiliateId => {
    const update = chainContactIds.filter(affiliate => !R.equals(affiliate, affiliateId));
    setValue('chainContactIds', update);
  };

  const showAlert = chainContactIds < 1 && (
    <Box
      bgcolor="success.light"
      border="none"
      borderRadius="4px"
      color="graphic.main"
      display="flex"
      height="80"
      m={3}
      p={3}
    >
      <InfoRoundedIcon sx={{ color: 'graphic.main' }} />
      <Typography sx={{ ...recipientRequiredStyles(theme) }} variant="body2">
        {RECIPIENT_REQUIRED}
      </Typography>
    </Box>
  );

  const moveUp = affiliateId => {
    const update = chainContactIds.slice();
    const index = update.findIndex(e => e === affiliateId);
    if (index > 0) {
      const el = update[index];
      update[index] = update[index - 1];
      update[index - 1] = el;
    }
    setValue('chainContactIds', update);
  };

  const moveDown = affiliateId => {
    const update = chainContactIds.slice();
    const index = update.findIndex(e => e === affiliateId);
    if (index !== -1 && index < update.length - 1) {
      const el = update[index];
      update[index] = update[index + 1];
      update[index + 1] = el;
    }
    setValue('chainContactIds', update);
  };

  const filteredAffiliateOptions = [
    ...affiliateOptions.filter(
      affiliate =>
        !chainContactIds.includes(parseInt(affiliate.value, 10)) &&
        parseInt(affiliate.value, 10) !== recipientIdName
    ),
  ];

  return (
    <Box display="flex" flexDirection="column" sx={{ ...sectionDivStyles(theme) }}>
      <Box mb={2} px={3}>
        <Typography variant="h7">Who should be in the Customer Chain?</Typography>
      </Box>
      <Box px={3}>
        <MAutocomplete
          defaultValue={{
            label: '',
            value: '',
          }}
          disableClearable
          id="chain-select"
          label="Recipient"
          loading={false}
          name="recipient"
          onChange={(_, val) => handleAddChain(val)}
          options={filteredAffiliateOptions}
        />
      </Box>
      <Box sx={{ marginTop: '24px' }} />
      {showAlert}
      {chainContactIds.map((chainContact, idx) => {
        const customer = affiliateOptions.find(
          ({ value }) => parseInt(value) === parseInt(chainContact)
        );
        return (
          <ChainedCustomer
            affiliateId={parseInt(customer.value, 10)}
            affiliateName={customer.label}
            index={idx}
            key={customer.value}
            maxIndex={chainContactIds.length - 1}
            onClickRemove={handleRemoveChain}
            onMoveDown={moveDown}
            onMoveUp={moveUp}
          />
        );
      })}
    </Box>
  );
};

SelectCustomerChain.propTypes = {
  affiliateOptions: PropTypes.arrayOf(PropTypes.shape({})),
  data: PropTypes.shape({
    chainContactIds: PropTypes.arrayOf(PropTypes.number),
    recipientIdName: PropTypes.string,
  }).isRequired,
  onAllRequiredFieldsSet: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
};

SelectCustomerChain.defaultProps = {
  affiliateOptions: [],
};

export default SelectCustomerChain;
